import { useEffect } from 'react';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { GIC_LIFECYCLE_EVENTS } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/constants.tsx';
import { useGICLifeCycleEvents } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/main.tsx';
import {
	fireTrackAnalytics,
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createActionsHook,
	createContainer,
	createHook,
	createStore,
	type Action,
} from '@atlassian/react-sweet-state';
import type { IssueRestoreMetaService } from '../../services/issue-restore-meta/index.tsx';
import type { IssueRestoreMetaControllerState } from './types.tsx';
import { convertStateToIssueRestoreMeta } from './utils.tsx';

export const initialState: IssueRestoreMetaControllerState = {
	isInitialised: false,
	isSavingCounter: 0,
	inProgressIssueMetas: [],
	restoreMetaError: undefined,
};
type ContainerProps = {
	issueRestoreMetaService: IssueRestoreMetaService;
};

type ModalOpenedParams = {
	inProgressIssueID: string;
	analyticsEvent?: UIAnalyticsEvent;
};

/**
 * IssueRestoreMeta contains the list of inProgressIssues that we need to restore
 */

export const actions = {
	init:
		(): Action<IssueRestoreMetaControllerState, ContainerProps> =>
		async ({ setState, getState }, { issueRestoreMetaService }) => {
			if (!getState().isInitialised) {
				try {
					const { getIssueRestoreMeta, createIssueRestoreMeta } = issueRestoreMetaService;
					let issueRestoreMeta = await getIssueRestoreMeta();
					if (!issueRestoreMeta) {
						issueRestoreMeta = await createIssueRestoreMeta({
							inProgressIssueMetas: [],
						});
					}

					if (issueRestoreMeta.inProgressIssueMetas !== undefined) {
						// filter out inProgressIssueMetas that shouldn't be restored
						const filteredinProgressIssueMetas = issueRestoreMeta.inProgressIssueMetas.filter(
							(inProgressIssueMeta) => inProgressIssueMeta.shouldRestore === true,
						);
						issueRestoreMeta.inProgressIssueMetas = filteredinProgressIssueMetas;
					}

					setState({ ...issueRestoreMeta, isInitialised: true });
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					fireErrorAnalytics({
						meta: {
							id: 'useIssueCreateMetaService',
							packageName: 'jiraIssueCreateRestore',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
					setState({ restoreMetaError: error });
				}
			}
		},
	onModalOpened:
		({
			inProgressIssueID,
			analyticsEvent,
		}: ModalOpenedParams): Action<IssueRestoreMetaControllerState, ContainerProps, Promise<void>> =>
		async ({ setState, getState }, { issueRestoreMetaService }) => {
			const currentState = getState();

			if (currentState.inProgressIssueMetas !== undefined) {
				if (
					!currentState.isInitialised ||
					currentState.inProgressIssueMetas
						.map((inProgressIssueMeta) => inProgressIssueMeta.inProgressIssueID)
						.includes(inProgressIssueID)
				) {
					return;
				}

				// TODO this needs to be updated for bulk create
				if (
					currentState.inProgressIssueMetas.find(
						(inProgressIssueMeta) => inProgressIssueMeta.shouldRestore,
					)
				) {
					analyticsEvent
						? fireTrackAnalytics(analyticsEvent, 'Existing inProgressIssue onModalOpened', {
								id: 'issueRestoreMetaOnModalOpened',
								packageName: 'jiraIssueCreateRestore',
							})
						: // something's gone wrong, lets fire some error analytics then recover
							fireErrorAnalytics({
								error: new Error('Existing inProgressIssue onModalOpened'),
								meta: {
									id: 'issueRestoreMetaOnModalOpened',
									packageName: 'jiraIssueCreateRestore',
								},
								sendToPrivacyUnsafeSplunk: true,
							});
				}
				setState({
					isSavingCounter: currentState.isSavingCounter + 1,
					inProgressIssueMetas: [{ inProgressIssueID, shouldRestore: true }],
				});
			}

			const { updateIssueRestoreMeta } = issueRestoreMetaService;

			try {
				await updateIssueRestoreMeta(convertStateToIssueRestoreMeta(getState()));
				setState({
					isSavingCounter: getState().isSavingCounter - 1,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useIssueCreateMetaService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
				setState({ restoreMetaError: error });
			}
		},
	onModalClosed:
		({
			inProgressIssueID: inProgressIssueIDToBeRemoved,
		}: {
			inProgressIssueID: string;
		}): Action<IssueRestoreMetaControllerState, ContainerProps, Promise<void>> =>
		async ({ setState, getState }, { issueRestoreMetaService }) => {
			const currentState = getState();

			const newInProgressIssueMetas = currentState.inProgressIssueMetas.filter(
				(inProgressIssueMeta) =>
					inProgressIssueMeta.inProgressIssueID !== inProgressIssueIDToBeRemoved,
			);
			setState({
				isSavingCounter: currentState.isSavingCounter + 1,
				restoreMetaError: undefined,
				inProgressIssueMetas: newInProgressIssueMetas,
			});
			const { updateIssueRestoreMeta } = issueRestoreMetaService;

			try {
				await updateIssueRestoreMeta(convertStateToIssueRestoreMeta(getState()));
				setState({
					isSavingCounter: getState().isSavingCounter - 1,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useIssueCreateMetaService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
				setState({ restoreMetaError: error });
			}
		},
	setShouldRestore:
		(
			inProgressIssueID: string,
			newShouldRestore: boolean,
		): Action<IssueRestoreMetaControllerState, ContainerProps, Promise<void>> =>
		async ({ getState, setState }, { issueRestoreMetaService }) => {
			const currentState = getState();

			const inProgressIssueMetaIndex = currentState.inProgressIssueMetas.findIndex(
				(inProgressIssueMeta) => inProgressIssueMeta.inProgressIssueID === inProgressIssueID,
			);
			if (inProgressIssueMetaIndex < 0) {
				return;
			}
			const newInProgressIssueMetas = [...currentState.inProgressIssueMetas];
			newInProgressIssueMetas[inProgressIssueMetaIndex] = {
				...newInProgressIssueMetas[inProgressIssueMetaIndex],
				shouldRestore: newShouldRestore,
			};

			setState({
				isSavingCounter: currentState.isSavingCounter + 1,
				inProgressIssueMetas: newInProgressIssueMetas,
			});

			const { updateIssueRestoreMeta } = issueRestoreMetaService;

			try {
				await updateIssueRestoreMeta(convertStateToIssueRestoreMeta(getState()));
				setState({
					isSavingCounter: getState().isSavingCounter - 1,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useIssueCreateMetaService',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
				setState({ restoreMetaError: error });
			}
		},
};

type Actions = typeof actions;

const Store = createStore<IssueRestoreMetaControllerState, Actions>({ initialState, actions });
export const useIssueRestoreMeta = createHook<IssueRestoreMetaControllerState, Actions>(Store);
export const IssueRestoreMetaContainer = createContainer<
	IssueRestoreMetaControllerState,
	Actions,
	ContainerProps
>(Store);
export const useIssueRestoreMetaActions = createActionsHook<
	IssueRestoreMetaControllerState,
	Actions
>(Store);

export const useIssueRestoreMetaSubscriptions = () => {
	const { onModalOpened, onModalClosed } = useIssueRestoreMetaActions();
	const { subscribe } = useGICLifeCycleEvents();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const analyticsEvent = createAnalyticsEvent({});
		const onOpenModal = (params: ModalOpenedParams) => onModalOpened({ ...params, analyticsEvent });
		return subscribe(GIC_LIFECYCLE_EVENTS.MODAL_OPENED, onOpenModal);
	}, [createAnalyticsEvent, onModalOpened, subscribe]);
	useEffect(
		() => subscribe(GIC_LIFECYCLE_EVENTS.MODAL_CLOSED, onModalClosed),
		[onModalClosed, subscribe],
	);
};
